<template>
  <div class="rebooking-handler">
    <Button
      v-if="selectedTravel && (!returnTravels.length || selectedReturnTravel)"
      @click="handleClick"
      theme="primary"
      class="rebooking-handler__button"
      :disabled="isButtonDisabled"
    >
      {{ $t('afterSale.rebook.confirm') }}
    </Button>
    <Button
      v-if="selectedTravel && returnTravels.length && !selectedReturnTravel"
      @click="handleClick"
      theme="primary"
      class="rebooking-handler__button"
    >
      {{ $t('booking.selectReturn') }}
    </Button>
  </div>
</template>

<script setup lang="ts">
  import { watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { Travel, Bundle } from '@/types/travelTypes'
  import Button from '@/components/BaseComponents/Button.vue'
  import { useRebookingState } from '@/composables/rebooking/useRebookingState'
  import { useRebookingProcess } from '@/composables/rebooking/useRebookingProcess'
  import { useTravelSelection } from '@/composables/rebooking/useTravelSelection'

  interface SelectedTravel extends Travel {
    routeIndex: number
    tariffCode: string
  }

  const props = defineProps<{
    selectedTravel: SelectedTravel | null
    selectedReturnTravel: SelectedTravel | null
    returnTravels: Travel[]
    showInboundTravels: () => void
    selectedBundle: Bundle | null
    bookingNumber: string
    itemsToReplace: string[]
  }>()

  const emit = defineEmits<{
    (e: 'selectReturn'): void
    (e: 'rebookingSuccess'): void
  }>()

  const route = useRoute()
  
  // Use composables
  const { 
    rebookingStarted,
    isButtonDisabled,
    setRebookingStarted,
    setErrorMessage
  } = useRebookingState()
  
  const { processSelectedTravel } = useTravelSelection()
  const { processRebooking } = useRebookingProcess(props.bookingNumber)

  const isInboundRebooking = route.query.rebookingType === 'inbound' && 
    route.query.entireJourney !== 'true'

  // Watch for changes in selected travel and process them
  watch(
    () => props.selectedTravel,
    (newTravel) => {
      if (newTravel) {
        processSelectedTravel(newTravel, 'outbound')
      }
    },
    { immediate: true }
  )

  // Watch for changes in selected return travel and process them
  watch(
    () => props.selectedReturnTravel,
    (newTravel) => {
      if (newTravel) {
        processSelectedTravel(newTravel, 'inbound')
      }
    }
  )

  /**
   * Main handler function for rebooking
   */
  const handleRebooking = async () => {
    await processRebooking(
      props.selectedTravel,
      props.selectedReturnTravel,
      props.itemsToReplace,
      setRebookingStarted,
      setErrorMessage,
      () => emit('rebookingSuccess')
    )
  }

  /**
   * Click handler for the rebooking button
   */
  const handleClick = () => {
    if (!props.selectedTravel) {
      console.error('No travel selected')
      return
    }

    if (!props.returnTravels?.length) {
      handleRebooking()
      return
    }
    if (props.returnTravels?.length && !props.selectedReturnTravel) {
      props.showInboundTravels?.()
      return
    }

    handleRebooking()
  }
</script>

<style lang="scss" scoped>
  .rebooking-handler {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__button {
      width: 100%;
    }

    .error-message {
      color: red;
      text-align: center;
      margin: 0;
    }
  }
</style>